import { useMutation } from '@apollo/client'
import { Calendar, FileFilled } from '@factorialco/f0-react/icons/app'
import {
  MeetingsOneOnOneSummaryDocument,
  MeetingsOneOnOneSummaryMutation,
} from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ONE_ON_ONE_TEMPLATE_TYPE } from 'modules/meetings/lib/constants/AIQueryTypes'
import { errorBlock } from 'modules/meetings/lib/constants/TipTapBlock'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'

export type MeetingState = 'pre' | 'post'

interface UseNotesTextEditorConfigProps {
  meetingId?: string | number
  meetingState?: MeetingState
}

export const useNotesTextEditorConfig = ({
  meetingId,
  meetingState,
}: UseNotesTextEditorConfigProps) => {
  const { canUseAI } = useMeeting()

  const [oneOnOneSummaryFormattedContent] = useMutation<MeetingsOneOnOneSummaryMutation>(
    MeetingsOneOnOneSummaryDocument
  )

  const handleGenerateOneOnOneSummary = async (templateType: string) => {
    track(trackingEvents.meetings.MeetingNotesPostMeeetingAIBlockStart, {
      meetingId: Number(meetingId),
      templateType,
    })

    if (meetingId && canUseAI) {
      try {
        const { data } = await oneOnOneSummaryFormattedContent({
          variables: {
            meetingId: Number(meetingId),
            templateType,
          },
        })
        if (data?.meetingsMutations?.generateMeetingsAiSummary?.aiSummary?.formattedContent) {
          const formattedContent =
            data.meetingsMutations.generateMeetingsAiSummary.aiSummary.formattedContent
          const parsedContent = JSON.parse(formattedContent)
          track(trackingEvents.meetings.MeetingNotesPostMeeetingAIBlockSuccess, {
            meetingId: Number(meetingId),
            templateType,
          })
          return parsedContent
        }
      } catch (error) {
        track(trackingEvents.meetings.MeetingNotesPostMeeetingAIBlockError, {
          meetingId: Number(meetingId),
          templateType,
        })
      }
    }
    return errorBlock(i18n.t('meetings.notes.labels.errorBlock.text'))
  }

  const toolbarLabels = {
    bold: i18n.t('rich_text_editor.toolbar.bold'),
    italic: i18n.t('rich_text_editor.toolbar.italic'),
    underline: i18n.t('rich_text_editor.toolbar.underline'),
    strike: i18n.t('rich_text_editor.toolbar.strike'),
    highlight: i18n.t('rich_text_editor.toolbar.highlight'),
    heading1: i18n.t('rich_text_editor.toolbar.heading1'),
    heading2: i18n.t('rich_text_editor.toolbar.heading2'),
    heading3: i18n.t('rich_text_editor.toolbar.heading3'),
    left: i18n.t('rich_text_editor.toolbar.left'),
    center: i18n.t('rich_text_editor.toolbar.center'),
    right: i18n.t('rich_text_editor.toolbar.right'),
    justify: i18n.t('rich_text_editor.toolbar.justify'),
    bulletList: i18n.t('rich_text_editor.toolbar.bulletList'),
    orderedList: i18n.t('rich_text_editor.toolbar.orderedList'),
    taskList: i18n.t('rich_text_editor.toolbar.taskList'),
    codeBlock: i18n.t('rich_text_editor.toolbar.codeBlock'),
    horizontalRule: i18n.t('rich_text_editor.toolbar.horizontalRule'),
    quote: i18n.t('rich_text_editor.toolbar.quote'),
    moreOptions: i18n.t('rich_text_editor.toolbar.moreOptions'),
    code: i18n.t('rich_text_editor.toolbar.code'),
    divider: i18n.t('rich_text_editor.toolbar.divider'),
    bullet: i18n.t('rich_text_editor.toolbar.bullet'),
    ordered: i18n.t('rich_text_editor.toolbar.ordered'),
    task: i18n.t('rich_text_editor.toolbar.task'),
    linkPlaceholder: i18n.t('rich_text_editor.toolbar.linkPlaceholder'),
    linkLabel: i18n.t('rich_text_editor.toolbar.linkLabel'),
    linkPaste: i18n.t('rich_text_editor.toolbar.linkPaste'),
    close: i18n.t('rich_text_editor.toolbar.close'),
    details: i18n.t('rich_text_editor.toolbar.dropdown'),
  }

  const oneOnOneButtons = [
    meetingState === 'post' && {
      type: ONE_ON_ONE_TEMPLATE_TYPE.POST_SUMMARY,
      emoji: '🗒️',
      label: '1:1 Summary',
      icon: FileFilled,
    },
    {
      type: ONE_ON_ONE_TEMPLATE_TYPE.PRE_SUMMARY,
      emoji: '🗓️',
      label: 'Last 1:1 recap',
      icon: Calendar,
    },
  ].filter(Boolean)

  const moodTrackerLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.moodTrackerLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.moodTrackerLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.moodTrackerLabels.collapse'),
  }

  const aiBlockLabels = {
    reset: i18n.t('meetings.notes.labels.aiBlockLabels.reset'),
    resetDescription: i18n.t('meetings.notes.labels.aiBlockLabels.resetDescription'),
    deleteBlock: i18n.t('meetings.notes.labels.aiBlockLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.aiBlockLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.aiBlockLabels.collapse'),
  }

  const slashCommandGroupLabels = {
    textStyles: i18n.t('meetings.notes.labels.slashCommandGroupLabels.textStyles'),
    lists: i18n.t('meetings.notes.labels.slashCommandGroupLabels.lists'),
    blocks: i18n.t('meetings.notes.labels.slashCommandGroupLabels.blocks'),
  }

  const transcriptLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.transcriptLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.transcriptLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.transcriptLabels.collapse'),
    messagesCount: i18n.t('meetings.notes.labels.transcriptLabels.messagesCount'),
    messagesCountSingular: i18n.t('meetings.notes.labels.transcriptLabels.messagesCountSingular'),
  }

  const liveCompanionLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.liveCompanionLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.liveCompanionLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.liveCompanionLabels.collapse'),
    oneTopicWithCommentary: i18n.t(
      'meetings.notes.labels.liveCompanionLabels.oneTopicWithCommentary'
    ),
    multipleTopicsWithCommentary: i18n.t(
      'meetings.notes.labels.liveCompanionLabels.multipleTopicsWithCommentary'
    ),
  }

  const chatLabels = {
    deleteBlock: i18n.t('meetings.notes.labels.chatLabels.deleteBlock'),
    expand: i18n.t('meetings.notes.labels.chatLabels.expand'),
    collapse: i18n.t('meetings.notes.labels.chatLabels.collapse'),
    messagesCount: i18n.t('meetings.notes.labels.chatLabels.messagesCount'),
    messagesCountSingular: 'message',
  }

  const labels = {
    toolbarLabels,
    slashCommandGroupLabels,
    moodTrackerLabels,
    aiBlockLabels,
    transcriptLabels,
    liveCompanionLabels,
    chatLabels,
    titlePlaceholder: i18n.t('meetings.notes.labels.titlePlaceholder'),
  }

  const getAiBlockConfig = () => {
    return {
      title:
        meetingState === 'post'
          ? i18n.t('meetings.meeting.post_meeting_buttons.title')
          : i18n.t('meetings.meeting.pre_meeting_buttons.title'),
      onClick: async (type: string) => {
        return await handleGenerateOneOnOneSummary(type)
      },
      buttons: canUseAI ? oneOnOneButtons : [],
    }
  }

  return {
    labels,
    aiBlockConfig: getAiBlockConfig(),
  }
}
